import React, { useState, Fragment, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useTicketPermissions } from '../../hooks/useTicketPermissions';
import { useIsServiceDeskUser } from '../../hooks/useIsServiceDeskUser';
import { trackProblemOperation } from '../../utils/appInsights';
import { toast } from 'react-hot-toast';
import ProblemStatusModal from '../../components/ProblemStatusModal';
import MoveToAssessModal from '../../components/MoveToAssessModal';
import MoveToAssess2Modal from '../../components/MoveToAssess2Modal';
import RootCauseAnalysisModal from '../../components/RootCauseAnalysisModal';
import CommunicateFixModal from '../../components/CommunicateFixModal';
import ClosureNotesModal from '../../components/ClosureNotesModal';
import AssignmentGroupChangeConfirmModal from '../../components/AssignmentGroupChangeConfirmModal';
import BreachReasonModal from '../../components/BreachReasonModal';
import AttachmentTab from '../../components/AttachmentTab';
import ActivityTab from '../../components/ActivityTab';
import RelatedRecordsTab from '../../components/RelatedRecordsTab';
import ProblemTasksTab from '../../components/ProblemTasksTab';
import TabLoadingSpinner from '../../components/TabLoadingSpinner';
import DuplicateProblemModal from '../../components/DuplicateProblemModal';
import CreateServiceRequestFromProblemModal from '../../components/CreateServiceRequestFromProblemModal';
import CreateChangeFromProblemModal from '../../components/CreateChangeFromProblemModal';
import RecordTimeModal from '../../components/RecordTimeModal';
import RichTextEditor from '../../components/RichTextEditor';
import RichTextDisplay from '../../components/RichTextDisplay';
import ResizableTextDisplay from '../../components/ResizableTextDisplay';
import AccessLostModal from '../../components/AccessLostModal';
import SlaInstancesDisplay, { areAllSlaInstancesTerminal } from '../../components/SlaInstancesDisplay';
import { stripHtml, sanitizeNoteInput, hasActualTextContent } from '../../utils/sanitize';
import { fetchAssignmentGroupMembers } from '../../utils/groupMembers';
import {
  impactLabels,
  urgencyLabels,
  priorityLabels,
  problemStateLabels,
  getDisplayLabel
} from '../../utils/labelMappings';
import {
  ArrowLeftIcon,
  PencilIcon,
  ClockIcon,
  CheckIcon,
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  TicketIcon,
  ChevronDownIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { Menu, Dialog, Transition } from '@headlessui/react';
import { dateToUTCNoon } from '../../utils/dateHelpers';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import DateTimePickerInput from '../../components/DateTimePickerInput';

interface WorkNote {
  id: string;
  User?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  note: string;
  timestamp: string;
  isPublic: boolean;
}

interface Problem {
  id: string;
  number: string;
  shortDescription: string;
  description: string;
  problemStatement?: string;
  state: string;
  problemState?: string;
  priority: string;
  impact: string;
  urgency: string;
  cause?: string;
  solution?: string;
  resolutionNotes?: string;
  closureNotes?: string;
  implementedDate?: string;
  resolvedAt?: string;
  closedAt?: string;
  resolvedBy?: {
    firstName: string;
    lastName: string;
  };
  closedBy?: {
    firstName: string;
    lastName: string;
  };
  createdAt: string;
  updatedAt: string;
  canEdit?: boolean;
  customerId?: string;
  assignedToId?: string;
  assignmentGroupId?: string;
  caller?: {
    id?: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  contact?: {
    id?: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  reportedBy?: {
    id?: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  assignedTo?: {
    id?: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  assignmentGroup?: {
    id?: string;
    name: string;
  };
  AssignmentGroup?: {
    id?: string;
    name: string;
  };
  customer?: {
    id?: string;
    name: string;
    shortName: string;
  };
  Customer?: {
    id?: string;
    accountName?: string;
    name?: string;
    accountShortName?: string;
    shortName?: string;
  };
  engagementId?: string;
  engagement?: {
    id?: string;
    name: string;
    status: string;
  };
  Engagement?: {
    id?: string;
    name: string;
    status: string;
  };
  resource?: {
    name: string;
    resourceId?: string;
  };
  Resource?: {
    name: string;
    resourceId?: string;
  };
  resourceIdValue?: string;
  otherResourceNames?: string;
  workNotes?: WorkNote[];
  assessment1Summary?: string;
  assessment2Summary?: string;
  workaround?: string;
  resolution?: string;
  solutionImplemented?: boolean;
  implementationDate?: string;
  ReferenceAlert?: {
    id: string;
    number: string;
    shortDescription: string;
    state: string;
    severity?: string;
    priority?: string;
  };
  // Review Matrix fields
  User_Case_rcaPresentedByIdToUser?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  reportedIssue?: string;
  problemDate?: string;
  actualServiceImpact?: string;
  briefAnalysis?: string;
  rootCause?: string;
  detailedAnalysis?: string;
  correctiveActions?: string;
  chronologyOfEvents?: string;
  contactId?: string;
  approvalGroupId?: string;
  assessment2Date?: string;
  customerApprovalRequested?: boolean;
  customerApprovalStatus?: string;
  User_Case_contactIdToUser?: {
    firstName: string;
    lastName: string;
    email: string;
  };
  pendingApprovals?: Array<{
    id: string;
    approverId: string;
    state: string;
    comments?: string;
  }>;
  breachReason?: string;
  breachReasonProvidedAt?: string;
  responseBreachReason?: string;
  ongoingBreachReason?: string;
  resolutionBreachReason?: string;
  canApproveOnBehalf?: boolean;
}

const priorityColors = {
  1: 'bg-red-100 text-red-800 border-red-200',
  2: 'bg-orange-100 text-orange-800 border-orange-200', 
  3: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  4: 'bg-green-100 text-green-800 border-green-200',
  5: 'bg-gray-100 text-gray-800 border-gray-200'
};

const priorityEnumToNumber = (priority: string): number => {
  switch(priority) {
    case 'critical_1': return 1;
    case 'high_2': return 2;
    case 'moderate_3': return 3;
    case 'low_4': return 4;
    case 'planning_5': return 5;
    default: return 3;
  }
};

const PROBLEM_STATE_TRANSITIONS = {
  new: ['assess_1'],
  assess_1: ['assess_2'],
  assess_2: ['root_cause_analysis'],
  root_cause_analysis: ['resolved'],
  resolved: ['closed', 'new'], // Can close or reopen to new
  closed: []
};

// Problem Workflow definitions
const problemWorkflow = [
  { id: 'new', name: 'New', icon: ClockIcon },
  { id: 'assess_1', name: 'Assess 1', icon: ExclamationTriangleIcon },
  { id: 'assess_2', name: 'Assess 2', icon: ExclamationTriangleIcon },
  { id: 'root_cause_analysis', name: 'Root Cause Analysis', icon: LightBulbIcon },
  { id: 'resolved', name: 'Resolved', icon: CheckIcon },
  { id: 'closed', name: 'Closed', icon: XMarkIcon }
];

const ProblemDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { formatDateTime, getToday } = useDateFormatter();
  const navigate = useNavigate();
  const location = useLocation();
  const problemsListSearch =
    (location.state as { problemsListSearch?: string } | null)?.problemsListSearch ?? '';
  const problemsListPath = `/problems${problemsListSearch}`;
  const queryClient = useQueryClient();
  const { isContact, isSuperAdmin, user } = useAuth();
  const isServiceDeskUser = useIsServiceDeskUser();
  const hasLoggedView = useRef(false);
  const previousProblemStateRef = useRef<string | null>(null);
  const [searchParams] = useSearchParams();

  // Get initial tab from URL parameter, default to 'details'
  const getInitialTab = (): 'details' | 'assessment' | 'activity' | 'notes' | 'attachments' | 'activities' | 'related' | 'tasks' | 'closure' => {
    const tabParam = searchParams.get('tab');
    const validTabs = ['details', 'assessment', 'activity', 'notes', 'attachments', 'activities', 'related', 'tasks', 'closure'];
    if (tabParam && validTabs.includes(tabParam)) {
      return tabParam as 'details' | 'assessment' | 'activity' | 'notes' | 'attachments' | 'activities' | 'related' | 'tasks' | 'closure';
    }
    return 'details';
  };

  const [activeTab, setActiveTab] = useState<'details' | 'assessment' | 'activity' | 'notes' | 'attachments' | 'activities' | 'related' | 'tasks' | 'closure'>(getInitialTab);

  // Watch for URL tab parameter changes and update active tab
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const validTabs = ['details', 'assessment', 'activity', 'notes', 'attachments', 'activities', 'related', 'tasks', 'closure'];
    if (tabParam && validTabs.includes(tabParam)) {
      setActiveTab(tabParam as typeof activeTab);
    }
  }, [searchParams]);

  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [moveToAssessModalOpen, setMoveToAssessModalOpen] = useState(false);
  const [moveToAssess2ModalOpen, setMoveToAssess2ModalOpen] = useState(false);
  const [rootCauseAnalysisModalOpen, setRootCauseAnalysisModalOpen] = useState(false);
  const [communicateFixModalOpen, setCommunicateFixModalOpen] = useState(false);
  const [isResendApproval, setIsResendApproval] = useState(false);
  const [selectedTargetState, setSelectedTargetState] = useState<string>('');
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState<'work_note' | 'customer_note'>('work_note');
  const [problemData, setProblemData] = useState<Problem | null>(null);

  // Duplicate, Create Service Request, and Create Change modal states
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [createServiceRequestModalOpen, setCreateServiceRequestModalOpen] = useState(false);
  const [createChangeModalOpen, setCreateChangeModalOpen] = useState(false);

  // Closure notes modal state
  const [closureNotesModalOpen, setClosureNotesModalOpen] = useState(false);

  // Record Time modal state
  const [recordTimeModalOpen, setRecordTimeModalOpen] = useState(false);

  // Access lost modal state
  const [accessLostModalOpen, setAccessLostModalOpen] = useState(false);

  // Breach reason modal state
  const [breachReasonModal, setBreachReasonModal] = useState<{
    isOpen: boolean;
    targetType?: 'RESPONSE' | 'ONGOING' | 'RESOLUTION';
  }>({
    isOpen: false,
    targetType: undefined
  });

  // Problem Date editing state
  const [isEditingProblemDate, setIsEditingProblemDate] = useState(false);
  const [editProblemDate, setEditProblemDate] = useState('');

  // Assignment group change confirmation modal state
  const [groupChangeConfirmModal, setGroupChangeConfirmModal] = useState<{
    isOpen: boolean;
    newGroupId: string | null;
    newGroupName: string;
    pendingChanges: any;
  }>({
    isOpen: false,
    newGroupId: null,
    newGroupName: '',
    pendingChanges: {}
  });

  // Fetch problem data
  const { data: problem, isLoading, error, refetch } = useQuery({
    queryKey: ['problem', id],
    queryFn: async () => {
      const response = await api.get(`/cases/${id}`);
      return response.data as Problem;
    },
    enabled: !!id,
    // Poll every 5 seconds when customer approval is pending to auto-refresh on approval
    refetchInterval: (query) => {
      const data = query.state.data as Problem | undefined;
      if (data?.customerApprovalRequested && data?.customerApprovalStatus === 'pending') {
        return 5000; // Poll every 5 seconds when awaiting approval
      }
      return false; // No polling otherwise
    }
  });

  const { data: workNotes = [], isLoading: isLoadingNotes } = useQuery<WorkNote[]>({
    queryKey: ['case', id, 'notes'],
    queryFn: async () => {
      const response = await api.get(`/cases/${id}/work-notes`);
      return response.data;
    },
    enabled: !!id && (activeTab === 'notes' || activeTab === 'activity'),
  });

  // Fetch SLA instances for this problem
  const { data: slaInstances } = useQuery({
    queryKey: ['sla-instances', 'PROBLEM', id],
    queryFn: async () => {
      const response = await api.get(`/sla-instances/PROBLEM/${id}`);
      return response.data;
    },
    enabled: !!id,
    // Stop refetching when all SLAs are in terminal states (BREACHED, ACHIEVED, COMPLETED, CANCELLED)
    refetchInterval: (query) => {
      const data = query.state.data;
      if (areAllSlaInstancesTerminal(data)) {
        return false; // Stop polling
      }
      return 30000; // Continue polling every 30 seconds
    }
  });

  // Get ticket permissions based on backend access control and user role
  const { canEdit, canEditAssignment, canChangeState, canAddWorkNote, canAddAttachments, canAddRelated, canPerformActions, editAccessReason } = useTicketPermissions(
    problem,
    'problems'
  );

  // Log "Viewed Problem Details" when problem is loaded
  useEffect(() => {
    if (problem && user && !hasLoggedView.current) {
      hasLoggedView.current = true;
      previousProblemStateRef.current = problem.problemState || null;
      trackProblemOperation(
        'Problem Details',
        user.email || user.firstName || 'Unknown',
        {
          action: 'Viewed Problem Details',
          problemNumber: problem.number || problem.id || id || 'Unknown',
        },
        {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          roleType: user.roleType,
          roleLevel: user.roleLevel,
          userClass: user.class,
          customerId: user.customerId,
          customerName: user.customerName,
          engagementId: user.engagementId,
          engagementName: user.engagementName,
          isServiceDeskUser: isServiceDeskUser,
        }
      );
    }
  }, [problem, user, id, isServiceDeskUser]);

  // Update previous state when problem state changes
  useEffect(() => {
    if (problem?.problemState) {
      previousProblemStateRef.current = problem.problemState;
    }
  }, [problem?.problemState]);

  // Fetch current assignment group members
  const { data: currentGroupMembersData } = useQuery({
    queryKey: ['group-members', problem?.assignmentGroupId, problem?.customerId || problem?.Customer?.id],
    queryFn: async () => {
      if (!problem?.assignmentGroupId) return [];
      try {
        return await fetchAssignmentGroupMembers(
          problem.assignmentGroupId,
          problem.customerId || problem.Customer?.id || null
        );
      } catch (error) {
        console.error('Error fetching group members:', error);
        return [];
      }
    },
    enabled: !!problem?.assignmentGroupId
  });

  // Mutation to add work notes
  const addNoteMutation = useMutation({
    mutationFn: async (data: { note: string; isPublic: boolean; noteType: 'work_note' | 'customer_note' }) => {
      const response = await api.post(`/problems/${id}/work-notes`, {
        note: data.note,
        isPublic: data.isPublic
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['problem', id] });
      queryClient.invalidateQueries({ queryKey: ['case', id, 'notes'] });
      queryClient.invalidateQueries({ queryKey: ['case-activities', id] });
      setNewNote('');
      
      // Log Note Added - distinguish between Work Note and Customer Note
      if (user && problem) {
        const action = variables.noteType === 'customer_note' ? 'Customer Note Added' : 'Work Note Added';
        trackProblemOperation(
          'Problem Details - Edit',
          user.email || user.firstName || 'Unknown',
          {
            action: action,
            problemNumber: problem.number || problem.id || id || 'Unknown',
          },
          {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          roleType: user.roleType,
          roleLevel: user.roleLevel,
          userClass: user.class,
          customerId: user.customerId,
          customerName: user.customerName,
          engagementId: user.engagementId,
          engagementName: user.engagementName,
          isServiceDeskUser: isServiceDeskUser,
          }
        );
      }
    }
  });

  // Breach reason mutation
  const breachReasonMutation = useMutation({
    mutationFn: async ({ breachReason, targetType }: { breachReason: string; targetType: 'RESPONSE' | 'ONGOING' | 'RESOLUTION' }) => {
      const response = await api.put(`/cases/${id}/breach-reason`, { breachReason, targetType });
      return response.data;
    },
    onSuccess: async () => {
      // Use refetchQueries to ensure data is fresh before closing modal
      await queryClient.refetchQueries({ queryKey: ['problem', id] });
      // Refetch list queries so breach reason shows without browser refresh
      queryClient.refetchQueries({ queryKey: ['problems'] });
      queryClient.refetchQueries({ queryKey: ['cases'] });
      toast.success('Breach reason saved successfully');
      setBreachReasonModal({ isOpen: false, targetType: undefined });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to save breach reason';
      toast.error(message);
    }
  });

  // Computed values for SLA breach status
  const hasSlaBreached = React.useMemo(() => {
    // Check problem's direct slaBreached field first
    if ((problem as any)?.slaBreached) return true;
    // Also check SLA instances
    if (!slaInstances) return false;
    return slaInstances.some((instance: any) =>
      instance.currentState === 'BREACHED' || instance.isBreached
    );
  }, [(problem as any)?.slaBreached, slaInstances]);

  // Check if breach reasons are provided for all breached SLAs
  const breachReasons = {
    response: problem?.responseBreachReason,
    ongoing: problem?.ongoingBreachReason,
    resolution: problem?.resolutionBreachReason
  };

  // Helper to check if a specific SLA target type is breached
  const isSlaTargetBreached = (targetType: string): boolean => {
    if (!slaInstances) return false;
    return slaInstances.some(
      (instance: any) =>
        // Check both instance.targetType and instance.SlaPolicy.targetType for compatibility
        (instance.targetType === targetType || instance.SlaPolicy?.targetType === targetType) &&
        (instance.currentState === 'BREACHED' || instance.isBreached)
    );
  };

  // Check if all breached SLAs have breach reasons
  const hasAllBreachReasons = (): boolean => {
    if (!hasSlaBreached) return true;

    // Check each breached SLA target type for its breach reason
    const responseBreached = isSlaTargetBreached('RESPONSE');
    const ongoingBreached = isSlaTargetBreached('ONGOING');
    const resolutionBreached = isSlaTargetBreached('RESOLUTION');

    if (responseBreached && !problem?.responseBreachReason) return false;
    if (ongoingBreached && !problem?.ongoingBreachReason) return false;
    if (resolutionBreached && !problem?.resolutionBreachReason) return false;

    return true;
  };

  // Legacy check for backward compatibility
  const hasBreachReason = hasAllBreachReasons();

  // Mutation to update problem (for Assign To Me)
  const updateProblemMutation = useMutation({
    mutationFn: async (data: { assignedToId: string }) => {
      const response = await api.put(`/cases/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['problem', id] });
      queryClient.invalidateQueries({ queryKey: ['problems'] });
      queryClient.invalidateQueries({ queryKey: ['case-activities', id] });
      toast.success('Problem assigned successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to assign problem');
    }
  });

  // Handler for Assign To Me button
  const handleAssignToMe = () => {
    if (user?.id) {
      updateProblemMutation.mutate({ assignedToId: user.id });
    }
  };

  // Check if Assign To Me button should be shown
  const shouldShowAssignToMeButton = () => {
    if (!user?.id || !problem) return false;
    const currentAssignedToId = problem.assignedToId || problem.assignedTo?.id || (problem.assignedTo as any)?._id;
    if (currentAssignedToId === user.id) return false;
    const isUserInGroup = (currentGroupMembersData || []).some((member: any) =>
      member.id === user.id || member.userId === user.id || member._id === user.id
    );
    // Show button if user is in the group (don't check canEdit - users in the group can assign to themselves)
    return isUserInGroup;
  };

  // Customer approval modal states
  const [showCustomerApprovalResponseModal, setShowCustomerApprovalResponseModal] = useState(false);
  const [customerApprovalDecision, setCustomerApprovalDecision] = useState<'approved' | 'rejected'>('approved');
  const [customerApprovalComments, setCustomerApprovalComments] = useState('');

  // Check if user has a pending customer approval (is in the pending approvers list)
  const userHasPendingCustomerApproval = (userId: string) => {
    if (!problem?.customerApprovalRequested || problem?.customerApprovalStatus !== 'pending') {
      return false;
    }
    return problem?.pendingApprovals?.some((a: any) => a.approverId === userId && a.state === 'pending') || false;
  };

  // Reopen modal states
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenReason, setReopenReason] = useState('');

  // Fetch problem tasks to check if any are open
  const { data: problemTasks } = useQuery({
    queryKey: ['problem-tasks', id],
    queryFn: async () => {
      const response = await api.get(`/cases/${id}/problem-tasks`);
      return response.data as Array<{ id: string; state: string; number: string }>;
    },
    enabled: !!id && !!problem?.problemState && ['assess_1', 'assess_2', 'root_cause_analysis', 'resolved', 'closed'].includes(problem.problemState),
  });

  // Check if there are any open problem tasks
  const hasOpenTasks = Boolean(problemTasks && problemTasks.length > 0 && problemTasks.some(task => task.state !== 'closed'));

  // Customer approval response mutation
  const customerApprovalMutation = useMutation({
    mutationFn: async (data: { decision: 'approved' | 'rejected', comments: string }) => {
      const response = await api.post(`/cases/${id}/customer-approval/respond`, {
        decision: data.decision,
        comments: data.comments
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['problem', id] });
      queryClient.invalidateQueries({ queryKey: ['problems'] });
      setShowCustomerApprovalResponseModal(false);
      setCustomerApprovalComments('');
      toast.success('Approval response submitted successfully');
      refetch();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to process customer approval');
    }
  });

  // Closure mutation
  const closureMutation = useMutation({
    mutationFn: async (closureNotes: string) => {
      const response = await api.patch(`/cases/${id}/problem-status`, {
        problemState: 'closed',
        closureNotes
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['problem', id] });
      queryClient.invalidateQueries({ queryKey: ['problems'] });
      queryClient.invalidateQueries({ queryKey: ['case-activities', id] });
      setClosureNotesModalOpen(false);
      toast.success('Problem closed successfully');
      refetch();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to close problem');
    }
  });

  // Reopen mutation
  const reopenMutation = useMutation({
    mutationFn: async (reopenReason: string) => {
      const response = await api.patch(`/cases/${id}/problem-status`, {
        problemState: 'new',
        reopenReason
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['problem', id] });
      queryClient.invalidateQueries({ queryKey: ['problems'] });
      queryClient.invalidateQueries({ queryKey: ['case-activities', id] });
      setShowReopenModal(false);
      setReopenReason('');
      toast.success('Problem reopened successfully');
      refetch();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to reopen problem');
    }
  });

  // Problem Date update mutation
  const updateProblemDateMutation = useMutation({
    mutationFn: async (problemDate: string) => {
      const response = await api.put(`/cases/${id}`, { problemDate });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['problem', id] });
      queryClient.invalidateQueries({ queryKey: ['problems'] });
      queryClient.invalidateQueries({ queryKey: ['case-activities', id] });
      setIsEditingProblemDate(false);
      toast.success('Problem Date updated successfully');
      refetch();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update Problem Date');
    }
  });

  // Handle Problem Date save
  const handleSaveProblemDate = () => {
    if (!editProblemDate) {
      toast.error('Problem Date is required');
      return;
    }
    // Check if date is in the future (date-only comparison to avoid timezone issues)
    const selectedDateStr = editProblemDate.split('T')[0];
    const todayStr = getToday();
    if (selectedDateStr > todayStr) {
      toast.error('Problem Date cannot be in the future');
      return;
    }
    // Convert to ISO format at UTC noon to prevent timezone-related date shifting
    const problemDateISO = dateToUTCNoon(editProblemDate);
    updateProblemDateMutation.mutate(problemDateISO);
  };

  // Handle Problem Date edit start
  const handleEditProblemDate = () => {
    setEditProblemDate(problem?.problemDate ? problem.problemDate.split('T')[0] : '');
    setIsEditingProblemDate(true);
  };

  // Update problemData when problem data changes
  React.useEffect(() => {
    if (problem) {
      setProblemData(problem);
    }
  }, [problem]);

  const handleStatusChange = (targetState: string) => {
    // Check if trying to resolve or close the problem with open tasks
    // Exception: "Communicate Fix" (requesting customer approval) is allowed even with open tasks
    const isRequestingCustomerApproval = targetState === 'resolved' &&
      problem?.problemState === 'root_cause_analysis' &&
      problem?.customerApprovalStatus !== 'approved';

    console.log('[DEBUG] handleStatusChange:', {
      targetState,
      problemState: problem?.problemState,
      customerApprovalStatus: problem?.customerApprovalStatus,
      hasOpenTasks,
      isRequestingCustomerApproval
    });

    // Check for breach reason before allowing resolved/closed transitions
    if ((targetState === 'resolved' || targetState === 'closed') && hasSlaBreached && !hasBreachReason) {
      // Find the first breached SLA without a reason
      let missingTargetType: 'RESPONSE' | 'ONGOING' | 'RESOLUTION' | undefined;
      if (isSlaTargetBreached('RESPONSE') && !problem?.responseBreachReason) {
        missingTargetType = 'RESPONSE';
      } else if (isSlaTargetBreached('ONGOING') && !problem?.ongoingBreachReason) {
        missingTargetType = 'ONGOING';
      } else if (isSlaTargetBreached('RESOLUTION') && !problem?.resolutionBreachReason) {
        missingTargetType = 'RESOLUTION';
      }

      if (missingTargetType) {
        const targetLabel = missingTargetType.charAt(0) + missingTargetType.slice(1).toLowerCase();
        toast.error(`Please provide a breach reason for the ${targetLabel} SLA before resolving or closing this ticket.`);
        setBreachReasonModal({ isOpen: true, targetType: missingTargetType });
        return;
      }
    }

    if (targetState === 'resolved' && hasOpenTasks && !isRequestingCustomerApproval) {
      toast.error('Cannot resolve problem. Please close all open problem tasks first.');
      return;
    }
    if (targetState === 'closed' && hasOpenTasks) {
      toast.error('Cannot close problem. Please close all open problem tasks first.');
      return;
    }

    setSelectedTargetState(targetState);

    // If reopening (moving to new from resolved), show reopen modal
    if (targetState === 'new' && problem?.problemState === 'resolved') {
      setShowReopenModal(true);
      return;
    }

    // If moving to assess_1, open the special MoveToAssessModal
    if (targetState === 'assess_1') {
      setMoveToAssessModalOpen(true);
    } else if (targetState === 'assess_2') {
      // If moving to assess_2, open the special MoveToAssess2Modal
      setMoveToAssess2ModalOpen(true);
    } else if (targetState === 'root_cause_analysis') {
      // If moving to root_cause_analysis, open the RootCauseAnalysisModal
      setRootCauseAnalysisModalOpen(true);
    } else if (targetState === 'resolved') {
      // If moving to resolved from root_cause_analysis
      if (problem?.problemState === 'root_cause_analysis') {
        // If customer already approved, go directly to resolved
        if (problem?.customerApprovalStatus === 'approved') {
          setStatusModalOpen(true);
        } else {
          // Customer hasn't approved yet, open CommunicateFixModal
          setIsResendApproval(false);
          setCommunicateFixModalOpen(true);
        }
      } else {
        setStatusModalOpen(true);
      }
    } else if (targetState === 'closed') {
      // If moving to closed, open ClosureNotesModal
      setClosureNotesModalOpen(true);
    } else {
      setStatusModalOpen(true);
    }
  };

  const handleStatusChangeSuccess = (updatedCase: Problem) => {
    // Update local state with new data to prevent error when access is lost
    setProblemData(updatedCase);

    // Log Problem State Updated if state changed
    if (user && previousProblemStateRef.current && updatedCase.problemState && previousProblemStateRef.current !== updatedCase.problemState) {
      trackProblemOperation(
        'Problem Details - Edit',
        user.email || user.firstName || 'Unknown',
        {
          action: 'Problem State Updated',
          problemNumber: updatedCase.number || problem?.number || id || 'Unknown',
          oldState: previousProblemStateRef.current,
          newState: updatedCase.problemState,
        },
        {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
          roleType: user.roleType,
          roleLevel: user.roleLevel,
          userClass: user.class,
          customerId: user.customerId,
          customerName: user.customerName,
          engagementId: user.engagementId,
          engagementName: user.engagementName,
          isServiceDeskUser: isServiceDeskUser,
        }
      );
      previousProblemStateRef.current = updatedCase.problemState;
    }

    // Check if user lost access after the update
    if (updatedCase.canEdit === false) {
      setAccessLostModalOpen(true);
      return;
    }

    refetch();
  };

  // Handler to show group change confirmation modal
  const handleShowGroupChangeConfirmation = (data: {
    newGroupId: string;
    newGroupName: string;
    pendingPayload: any;
  }) => {
    setGroupChangeConfirmModal({
      isOpen: true,
      newGroupId: data.newGroupId,
      newGroupName: data.newGroupName,
      pendingChanges: data.pendingPayload
    });
  };

  // Handler for confirming assignment group change
  const handleConfirmGroupChange = async () => {
    const payload = groupChangeConfirmModal.pendingChanges;

    try {
      // Determine which endpoint to use based on the payload
      // assess_2 and root_cause_analysis states use /problem-status endpoint
      // assess_1 uses /cases/:id PUT endpoint
      if (payload.problemState === 'assess_2' || payload.problemState === 'root_cause_analysis') {
        await api.patch(`/cases/${id}/problem-status`, payload);
      } else {
        await api.put(`/cases/${id}`, payload);
      }

      // Close modal
      setGroupChangeConfirmModal({
        isOpen: false,
        newGroupId: null,
        newGroupName: '',
        pendingChanges: {}
      });

      // Show success message and redirect
      toast.success('Problem status updated successfully. You no longer have access to this problem.');
      queryClient.invalidateQueries({ queryKey: ['problems'] });
      navigate(problemsListPath);
    } catch (error: any) {
      console.error('Failed to update problem status:', error);
      toast.error(error.response?.data?.message || 'Failed to update problem status');

      // Close modal on error
      setGroupChangeConfirmModal({
        isOpen: false,
        newGroupId: null,
        newGroupName: '',
        pendingChanges: {}
      });
    }
  };

  // Handler for cancelling assignment group change
  const handleCancelGroupChange = () => {
    setGroupChangeConfirmModal({
      isOpen: false,
      newGroupId: null,
      newGroupName: '',
      pendingChanges: {}
    });
  };

  const handleAddNote = (e: React.FormEvent) => {
    e.preventDefault();
    const sanitizedNote = sanitizeNoteInput(newNote);
    if (!hasActualTextContent(sanitizedNote)) return;

    addNoteMutation.mutate({
      note: sanitizedNote,
      isPublic: isContact() ? true : (noteType === 'customer_note'),  // Customer users always create public notes
      noteType: noteType  // Pass noteType for logging
    });
  };

  // Render AccessLostModal at top level so it always shows when open
  if (accessLostModalOpen) {
    return (
      <>
        <AccessLostModal
          isOpen={accessLostModalOpen}
          onConfirm={() => {
            setAccessLostModalOpen(false);
            navigate(problemsListPath);
          }}
          ticketNumber={problem?.number}
        />
      </>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading problem details...</div>
      </div>
    );
  }

  if (error || !problem) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-red-600">Failed to load problem details</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-8 py-6">
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={() => navigate(problemsListPath)}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Problems
          </button>

          <div className="flex gap-2">
            {/* Assign To Me Button */}
            {shouldShowAssignToMeButton() && (
              <button
                onClick={handleAssignToMe}
                disabled={updateProblemMutation.isPending}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {updateProblemMutation.isPending ? 'Assigning...' : 'Assign To Me'}
              </button>
            )}

            {/* Record Time Button - Show when assigned to current user and state is not new/closed/cancelled */}
            {problemData &&
             user &&
             (problemData.assignedTo?.id === user.id || problemData.assignedToId === user.id) &&
             problemData.problemState !== 'new' &&
             problemData.problemState !== 'closed' &&
             problemData.problemState !== 'cancelled' &&
             problemData.Customer &&
             problemData.Engagement &&
             problemData.AssignmentGroup && (
              <button
                onClick={() => setRecordTimeModalOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <ClockIcon className="h-5 w-5 mr-2" />
                Record Time
              </button>
            )}

            {/* Actions Dropdown - Show if user can perform actions (not state-restricted) */}
            {canPerformActions && (
            <Menu as="div" className="relative inline-block text-left">
            <div>
              <Menu.Button className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                <TicketIcon className="h-5 w-5 mr-2" />
                Actions
                <ChevronDownIcon className="ml-2 h-4 w-4" />
              </Menu.Button>
            </div>

            <Menu.Items className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
              <div className="py-1">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => setDuplicateModalOpen(true)}
                      className={`${
                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                      } flex items-center w-full px-4 py-2 text-sm`}
                    >
                      <TicketIcon className="h-5 w-5 mr-3" />
                      Duplicate Problem
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => setCreateServiceRequestModalOpen(true)}
                      className={`${
                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                      } flex items-center w-full px-4 py-2 text-sm`}
                    >
                      <ChatBubbleLeftRightIcon className="h-5 w-5 mr-3" />
                      Create Service Request
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => setCreateChangeModalOpen(true)}
                      className={`${
                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                      } flex items-center w-full px-4 py-2 text-sm`}
                    >
                      <PencilIcon className="h-5 w-5 mr-3" />
                      Create Change
                    </button>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Menu>
            )}
          </div>
        </div>

        <WorkflowProgressComponent problem={problem} onStatusChange={handleStatusChange} onResendApproval={() => { setIsResendApproval(true); setCommunicateFixModalOpen(true); }} hasOpenTasks={hasOpenTasks} canEdit={canEdit} canChangeState={canChangeState} />

        {/* Open Problem Tasks Warning Banner - Only show after customer approval/rejection */}
        {(problem.problemState === 'root_cause_analysis' || problem.problemState === 'resolved') &&
         hasOpenTasks &&
         (problem.customerApprovalStatus === 'approved' || problem.customerApprovalStatus === 'rejected') && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Problem Tasks Are Open</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>Cannot {problem.problemState === 'root_cause_analysis' ? 'resolve' : 'close'} this problem because there are open problem tasks. Please close all problem tasks first.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Customer Approval Banner - For Contacts */}
        {problem.customerApprovalRequested &&
         problem.customerApprovalStatus === 'pending' &&
         isContact() &&
         (user?.id === problem.contactId || (!problem.contactId && user?.engagementId === (problem.Engagement as any)?.id)) && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-yellow-800">Customer Approval Required</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>This change request requires your approval. Please review the details and provide your decision.</p>
                </div>
                <div className="mt-4 flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setCustomerApprovalDecision('approved');
                      setShowCustomerApprovalResponseModal(true);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCustomerApprovalDecision('rejected');
                      setShowCustomerApprovalResponseModal(true);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Reject
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Customer Approval Banner - For Customer-Approver Group Members */}
        {problem.customerApprovalRequested &&
         problem.customerApprovalStatus === 'pending' &&
         !isContact() &&
         problem.pendingApprovals?.some((a: any) => a.approverId === user?.id && a.state === 'pending') && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-yellow-800">Customer Approval Required</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>Waiting for customer approval. Approval request has been sent to the contacts and customer approval group.</p>
                </div>
                <div className="mt-4 flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setCustomerApprovalDecision('approved');
                      setShowCustomerApprovalResponseModal(true);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCustomerApprovalDecision('rejected');
                      setShowCustomerApprovalResponseModal(true);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Reject
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Customer Approval Banner - Read-only for Other Agents (who cannot approve on behalf) */}
        {problem.customerApprovalRequested &&
         problem.customerApprovalStatus === 'pending' &&
         !isContact() &&
         !problem.pendingApprovals?.some((a: any) => a.approverId === user?.id && a.state === 'pending') &&
         !(problem.canApproveOnBehalf === true || isSuperAdmin()) && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-yellow-800">Customer Approval Required</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>Waiting for customer approval. Approval request has been sent to the contacts and customer approval group.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Manager Approval Banner - Show when customer_approver group member or super_admin can approve on behalf of customer */}
        {problem.customerApprovalRequested &&
         problem.customerApprovalStatus === 'pending' &&
         !isContact() &&
         !userHasPendingCustomerApproval((user as any)?.id) &&
         (problem.canApproveOnBehalf === true || isSuperAdmin()) && (
          <div className="mt-6 rounded-lg p-4 bg-blue-50 border border-blue-200 mb-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-blue-800">
                  Manager Approval on Behalf of Customer
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>As a member of the customer approver group, you can approve this problem fix on behalf of the customer. The customer will be notified of your decision.</p>
                </div>
                <div className="mt-4 flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setCustomerApprovalDecision('approved');
                      setShowCustomerApprovalResponseModal(true);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <CheckIcon className="h-5 w-5 mr-2" />
                    Approve on Behalf of Customer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mandatory Fields Warning Notification */}
        {(() => {
          const missingFields: string[] = [];

          if (!problem.assignedTo) {
            missingFields.push('Assigned To');
          }
          if (!problem.User_Case_rcaPresentedByIdToUser) {
            missingFields.push('RCA Presented By');
          }
          if (!problem.problemDate) {
            missingFields.push('Problem Date');
          }
          if (!problem.reportedIssue?.trim()) {
            missingFields.push('Reported Issue');
          }
          if (!problem.actualServiceImpact?.trim()) {
            missingFields.push('Actual Service Impact');
          }
          if (!problem.briefAnalysis?.trim()) {
            missingFields.push('Brief Analysis');
          }
          if (!problem.rootCause?.trim()) {
            missingFields.push('Root Cause');
          }
          if (!problem.detailedAnalysis?.trim()) {
            missingFields.push('Detailed Analysis');
          }
          if (!problem.correctiveActions?.trim()) {
            missingFields.push('Corrective/Mitigate Actions');
          }
          if (!problem.chronologyOfEvents?.trim()) {
            missingFields.push('Chronology of Events');
          }

          if (missingFields.length > 0) {
            return (
              <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md shadow-sm">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Incomplete Review Matrix
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>
                        Please provide <strong>Assigned To</strong> and complete the <strong>Review Matrix</strong> fields before moving to the next state.
                      </p>
                      <p className="mt-2">
                        <strong>Missing fields:</strong> {missingFields.join(', ')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          return null;
        })()}

        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-6 w-6 text-gray-400" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">
                    {problem.number}{problem.shortDescription && <span className="text-base font-normal text-gray-600"> - {stripHtml(problem.shortDescription)}</span>}
                  </h1>
                  <p className="mt-1 text-sm text-gray-500">
                    Created on {formatDateTime(problem.createdAt)}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {/* Priority Badge */}
                {(() => {
                  const priorityNum = priorityEnumToNumber(problem.priority);
                  const colorClass = priorityColors[priorityNum as keyof typeof priorityColors] || priorityColors[3];
                  const label = getDisplayLabel(problem.priority, priorityLabels);
                  return (
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${colorClass}`}>
                      Priority {priorityNum} - {label}
                    </span>
                  );
                })()}

                {/* Only show Edit button for problems that can be edited */}
                {canEdit && !problem.customerApprovalRequested && (
                  <Link
                    to={`/problems/${id}/edit`}
                    className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    title={editAccessReason}
                  >
                    <PencilIcon className="h-4 w-4 mr-1" />
                    Edit
                  </Link>
                )}
                {canEditAssignment && !canEdit && !problem.customerApprovalRequested && (
                  <Link
                    to={`/problems/${id}/edit`}
                    className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    title={editAccessReason}
                  >
                    <PencilIcon className="h-4 w-4 mr-1" />
                    Edit Assignment
                  </Link>
                )}

                {/* Show Problem Tasks button when state is assess_1 or later (not new or cancelled), and customer approval has not been requested */}
                {problem.problemState && ['assess_1', 'assess_2', 'root_cause_analysis', 'resolved', 'closed'].includes(problem.problemState) &&
                 !problem.customerApprovalRequested && (
                  <button
                    onClick={() => setActiveTab('tasks')}
                    className="inline-flex items-center px-3 py-1 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm"
                  >
                    <TicketIcon className="h-4 w-4 mr-1" />
                    Problem Tasks
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* SLA Timers */}
          <SlaInstancesDisplay
            slaInstances={slaInstances}
            onBreachDetected={() => {
              // Trigger immediate refetch when UI detects breach
              queryClient.invalidateQueries({ queryKey: ['sla-instances', 'PROBLEM', id] });
            }}
            breachReasons={breachReasons}
            onRequestBreachReason={(targetType) => setBreachReasonModal({ isOpen: true, targetType })}
          />

          <div className="border-b border-gray-200">
            <nav className="-mb-px flex">
              <button
                onClick={() => setActiveTab('details')}
                className={`py-2 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'details'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Details
              </button>
              <button
                onClick={() => setActiveTab('assessment')}
                className={`py-2 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'assessment'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Assessment Summary
              </button>
              <button
                onClick={() => setActiveTab('activity')}
                className={`py-2 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'activity'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Notes
              </button>
              <button
                onClick={() => setActiveTab('attachments')}
                className={`py-2 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'attachments'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Attachments
              </button>
              <button
                onClick={() => setActiveTab('activities')}
                className={`py-2 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'activities'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Activities
              </button>
              <button
                onClick={() => setActiveTab('related')}
                className={`py-2 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'related'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Related Records
              </button>
              {/* Show Problem Tasks tab when state is assess_1 or later (not new or cancelled) */}
              {problem.problemState && ['assess_1', 'assess_2', 'root_cause_analysis', 'resolved', 'closed'].includes(problem.problemState) && (
                <button
                  onClick={() => setActiveTab('tasks')}
                  className={`py-2 px-6 border-b-2 font-medium text-sm ${
                    activeTab === 'tasks'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Problem Tasks
                </button>
              )}
              <button
                onClick={() => setActiveTab('closure')}
                className={`py-2 px-6 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === 'closure'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Closure
                {(problem.problemState === 'resolved' || problem.problemState === 'closed' || problem.problemState === 'cancelled') && (
                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    {problem.problemState === 'cancelled' ? '!' : '✓'}
                  </span>
                )}
              </button>
            </nav>
          </div>

          <div className="p-6">
            <TabContent
              activeTab={activeTab}
              problem={problem}
              problemId={id!}
              newNote={newNote}
              setNewNote={setNewNote}
              noteType={noteType}
              setNoteType={setNoteType}
              handleAddNote={handleAddNote}
              isAddingNote={addNoteMutation.isPending}
              workNotes={workNotes}
              isLoadingNotes={isLoadingNotes}
              canEdit={canEdit}
              canAddWorkNote={canAddWorkNote}
              canAddAttachments={canAddAttachments}
              canAddRelated={canAddRelated}
              handleAssignToMe={handleAssignToMe}
              shouldShowAssignToMeButton={shouldShowAssignToMeButton}
              isAssigning={updateProblemMutation.isPending}
              isEditingProblemDate={isEditingProblemDate}
              editProblemDate={editProblemDate}
              setEditProblemDate={setEditProblemDate}
              handleSaveProblemDate={handleSaveProblemDate}
              handleEditProblemDate={handleEditProblemDate}
              setIsEditingProblemDate={setIsEditingProblemDate}
              isUpdatingProblemDate={updateProblemDateMutation.isPending}
              onTabChange={setActiveTab}
              user={user}
              id={id!}
              isServiceDeskUser={isServiceDeskUser}
            />
          </div>
        </div>
      </div>

      {/* Problem Status Modal */}
      <ProblemStatusModal
        isOpen={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        onSuccess={handleStatusChangeSuccess}
        caseId={id || ''}
        currentState={problem.problemState || 'new'}
        targetState={selectedTargetState}
        actionLabel={`Move to ${getDisplayLabel(selectedTargetState, problemStateLabels)}`}
      />

      {/* Move to Assess Modal */}
      <MoveToAssessModal
        isOpen={moveToAssessModalOpen}
        onClose={() => setMoveToAssessModalOpen(false)}
        onSuccess={handleStatusChangeSuccess}
        problemId={id || ''}
        currentState={problem.problemState || 'new'}
        currentProblem={problem}
        onShowGroupChangeConfirmation={handleShowGroupChangeConfirmation}
      />

      {/* Move to Assess 2 Modal */}
      <MoveToAssess2Modal
        isOpen={moveToAssess2ModalOpen}
        onClose={() => setMoveToAssess2ModalOpen(false)}
        onSuccess={handleStatusChangeSuccess}
        currentProblem={problem}
        onShowGroupChangeConfirmation={handleShowGroupChangeConfirmation}
        problemId={id || ''}
        currentState={problem.problemState || 'new'}
      />

      {/* Root Cause Analysis Modal */}
      <RootCauseAnalysisModal
        isOpen={rootCauseAnalysisModalOpen}
        onClose={() => setRootCauseAnalysisModalOpen(false)}
        onConfirm={async (rcaData) => {
          try {
            // Convert date to ISO 8601 format
            const assessment2DateISO = dateToUTCNoon(rcaData.assessment2Date);

            const response = await api.patch(`/cases/${id}/problem-status`, {
              problemState: 'root_cause_analysis',
              assignmentGroupId: rcaData.assignmentGroupId,
              assignedTo: rcaData.assignedToId,
              assessment2Date: assessment2DateISO,
              assessment2Summary: rcaData.assessment2Summary,
              ...(problem?.problemDate && { problemDate: problem.problemDate })
            });
            handleStatusChangeSuccess(response.data);
            setRootCauseAnalysisModalOpen(false);
          } catch (error: any) {
            console.error('Error moving to Root Cause Analysis:', error);
            throw error; // Re-throw so the modal can handle the error state
          }
        }}
        problemId={id || ''}
        currentProblem={problem}
        onShowGroupChangeConfirmation={handleShowGroupChangeConfirmation}
      />

      {/* Communicate Fix Modal */}
      <CommunicateFixModal
        isOpen={communicateFixModalOpen}
        onClose={() => {
          setCommunicateFixModalOpen(false);
          setIsResendApproval(false);
        }}
        onConfirm={() => {
          refetch();
          queryClient.invalidateQueries({ queryKey: ['case-activities', id] });
        }}
        problemId={id || ''}
        problemNumber={problem.number}
        contactId={problem.contactId}
        approvalGroupId={problem.approvalGroupId}
        engagementId={problem.engagementId}
        isResend={isResendApproval}
      />

      {/* Duplicate Problem Modal */}
      <DuplicateProblemModal
        isOpen={duplicateModalOpen}
        onClose={() => setDuplicateModalOpen(false)}
        problem={problem}
        onSuccess={(newProblemNumber) => {
          if (user && problem) {
            trackProblemOperation(
              'Problem Created',
              user.email || user.firstName || 'Unknown',
              {
                action: 'Duplicate Problem',
                problemNumber: newProblemNumber,
              },
              {
                firstName: user.firstName,
                lastName: user.lastName,
                role: user.role,
                roleType: user.roleType,
                roleLevel: user.roleLevel,
                userClass: user.class,
                customerId: user.customerId,
                customerName: user.customerName,
                engagementId: user.engagementId,
                engagementName: user.engagementName,
                isServiceDeskUser: isServiceDeskUser,
              }
            );
          }
        }}
      />

      {/* Create Service Request from Problem Modal */}
      <CreateServiceRequestFromProblemModal
        isOpen={createServiceRequestModalOpen}
        onClose={() => setCreateServiceRequestModalOpen(false)}
        problem={problem}
        onSuccess={(serviceRequestNumber) => {
          if (user && problem) {
            trackProblemOperation(
              'Problem Details - Service Request Created',
              user.email || user.firstName || 'Unknown',
              {
                action: 'Created Service Request',
                problemNumber: problem.number || problem.id || id || 'Unknown',
                serviceRequestNumber: serviceRequestNumber,
              },
              {
                firstName: user.firstName,
                lastName: user.lastName,
                role: user.role,
                roleType: user.roleType,
                roleLevel: user.roleLevel,
                userClass: user.class,
                customerId: user.customerId,
                customerName: user.customerName,
                engagementId: user.engagementId,
                engagementName: user.engagementName,
                isServiceDeskUser: isServiceDeskUser,
              }
            );
          }
        }}
      />

      {/* Create Change from Problem Modal */}
      <CreateChangeFromProblemModal
        isOpen={createChangeModalOpen}
        onClose={() => setCreateChangeModalOpen(false)}
        problem={problem}
        onSuccess={(changeNumber) => {
          if (user && problem) {
            trackProblemOperation(
              'Problem Details - Change Created',
              user.email || user.firstName || 'Unknown',
              {
                action: 'Created Change',
                problemNumber: problem.number || problem.id || id || 'Unknown',
                changeNumber: changeNumber,
              },
              {
                firstName: user.firstName,
                lastName: user.lastName,
                role: user.role,
                roleType: user.roleType,
                roleLevel: user.roleLevel,
                userClass: user.class,
                customerId: user.customerId,
                customerName: user.customerName,
                engagementId: user.engagementId,
                engagementName: user.engagementName,
                isServiceDeskUser: isServiceDeskUser,
              }
            );
          }
        }}
      />

      {/* Closure Notes Modal */}
      <ClosureNotesModal
        isOpen={closureNotesModalOpen}
        onClose={() => setClosureNotesModalOpen(false)}
        onConfirm={(closureNotes) => closureMutation.mutate(closureNotes)}
        title="Close Problem"
        message="Please provide closure notes explaining why this problem is being closed. This information will be recorded in the problem history."
        confirmLabel="Close Problem"
        cancelLabel="Cancel"
        confirmButtonClass="bg-gray-600 hover:bg-gray-700 text-white"
        isLoading={closureMutation.isPending}
      />

      {/* Reopen Modal */}
      <Transition appear show={showReopenModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setShowReopenModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                    Reopen Problem
                  </Dialog.Title>

                  <div className="mt-2">
                    <p className="text-sm text-gray-500 mb-4">
                      Please provide a reason for reopening this problem. This information will be recorded in the problem history.
                    </p>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Reopen Reason <span className="text-red-500">*</span>
                      </label>
                      <RichTextEditor
                        value={reopenReason}
                        onChange={setReopenReason}
                        placeholder="Explain why this problem needs to be reopened..."
                        minHeight="150px"
                      />
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                      onClick={() => {
                        setShowReopenModal(false);
                        setReopenReason('');
                      }}
                      disabled={reopenMutation.isPending}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 disabled:opacity-50"
                      onClick={() => {
                        if (!reopenReason.trim() || reopenReason === '<p><br></p>') {
                          toast.error('Please provide a reopen reason');
                          return;
                        }
                        reopenMutation.mutate(reopenReason);
                      }}
                      disabled={reopenMutation.isPending}
                    >
                      {reopenMutation.isPending ? 'Reopening...' : 'Reopen Problem'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Customer Approval Response Modal */}
      <Transition appear show={showCustomerApprovalResponseModal} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setShowCustomerApprovalResponseModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="sm:flex sm:items-start">
                    <div className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
                      customerApprovalDecision === 'approved' ? 'bg-green-100' : 'bg-red-100'
                    }`}>
                      {customerApprovalDecision === 'approved' ? (
                        <CheckIcon className="h-6 w-6 text-green-600" />
                      ) : (
                        <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                        </svg>
                      )}
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                        {customerApprovalDecision === 'approved' ? 'Approve Problem Fix' : 'Reject Problem Fix'}
                      </Dialog.Title>
                      <div className="mt-4">
                        <label htmlFor="customer-approval-comments" className="block text-sm font-medium text-gray-700 mb-2">
                          Comments {customerApprovalDecision === 'rejected' && <span className="text-red-600">*</span>}
                        </label>
                        <RichTextEditor
                          value={customerApprovalComments}
                          onChange={(value) => setCustomerApprovalComments(value)}
                          placeholder={customerApprovalDecision === 'approved' ? 'Add optional comments...' : 'Please explain why you are rejecting this fix...'}
                          minHeight="120px"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 mt-4">
                    <button
                      type="button"
                      disabled={customerApprovalDecision === 'rejected' && !customerApprovalComments.trim()}
                      className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto ${
                        customerApprovalDecision === 'approved'
                          ? 'bg-green-600 hover:bg-green-500 disabled:bg-green-300'
                          : 'bg-red-600 hover:bg-red-500 disabled:bg-red-300'
                      } disabled:cursor-not-allowed`}
                      onClick={() => {
                        customerApprovalMutation.mutate({
                          decision: customerApprovalDecision,
                          comments: customerApprovalComments
                        });
                      }}
                    >
                      {customerApprovalDecision === 'approved' ? 'Confirm Approval' : 'Confirm Rejection'}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={() => {
                        setShowCustomerApprovalResponseModal(false);
                        setCustomerApprovalComments('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Record Time Modal */}
      {problemData && problemData.Customer && problemData.Engagement && problemData.AssignmentGroup && (
        <RecordTimeModal
          isOpen={recordTimeModalOpen}
          onClose={() => setRecordTimeModalOpen(false)}
          onSuccess={() => {
            // Optionally refresh the problem data after recording time
            queryClient.invalidateQueries({ queryKey: ['problem', id] });
          }}
          ticketNumber={problemData.number || ''}
          customerName={problemData.Customer.accountName || problemData.Customer.name || ''}
          customerId={problemData.customerId || problemData.Customer.id || ''}
          engagementName={problemData.Engagement.name || ''}
          engagementId={problemData.engagementId || problemData.Engagement.id || ''}
          groupName={problemData.AssignmentGroup.name || ''}
          groupId={problemData.assignmentGroupId || problemData.AssignmentGroup.id || ''}
        />
      )}

      {/* Breach Reason Modal */}
      <BreachReasonModal
        isOpen={breachReasonModal.isOpen}
        onClose={() => setBreachReasonModal({ isOpen: false, targetType: undefined })}
        onConfirm={(reason, targetType) => {
          if (targetType) {
            breachReasonMutation.mutate({ breachReason: reason, targetType });
          }
        }}
        targetType={breachReasonModal.targetType}
        isLoading={breachReasonMutation.isPending}
      />

      {/* Assignment Group Change Confirmation Modal */}
      <AssignmentGroupChangeConfirmModal
        isOpen={groupChangeConfirmModal.isOpen}
        onConfirm={handleConfirmGroupChange}
        onCancel={handleCancelGroupChange}
        newGroupName={groupChangeConfirmModal.newGroupName}
        ticketNumber={problem?.number}
      />

    </div>
  );
};

// Separate component for workflow progress to avoid JSX complexity
const WorkflowProgressComponent: React.FC<{ problem: Problem; onStatusChange: (targetState: string) => void; onResendApproval: () => void; hasOpenTasks: boolean; canEdit: boolean; canChangeState: boolean }> = ({ problem, onStatusChange, onResendApproval, hasOpenTasks, canEdit: _canEdit, canChangeState }) => {
  // Use the new Problem workflow for all Problem tickets
  const workflow = problemWorkflow;
  const currentState = problem.problemState || 'new';

  const availableTransitions = PROBLEM_STATE_TRANSITIONS[currentState as keyof typeof PROBLEM_STATE_TRANSITIONS] || [];
  
  const getCurrentStepIndex = () => {
    return workflow.findIndex(step => step.id === currentState);
  };

  const currentIdx = getCurrentStepIndex();
  const isCancelled = currentState === 'cancelled';

  return (
    <div className="bg-white shadow rounded-lg mb-6 p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Problem Workflow</h3>
      <nav aria-label="Progress">
        <ol className="flex items-center">
          {workflow.map((step, stepIdx) => {
            const Icon = step.icon;
            const isComplete = stepIdx < currentIdx;
            const isCurrent = stepIdx === currentIdx;

            return (
              <li key={step.id} className={stepIdx !== workflow.length - 1 ? 'flex-1' : ''}>
                <div className="flex items-center">
                  <div
                    className={`relative flex h-10 w-10 items-center justify-center rounded-full ${
                      isComplete ? 'bg-indigo-600' :
                      isCurrent ? 'bg-indigo-600' :
                      'bg-gray-300'
                    } ${isCancelled ? 'opacity-50' : ''}`}
                  >
                    <Icon className={`h-6 w-6 ${
                      isComplete || isCurrent ? 'text-white' : 'text-gray-600'
                    }`} />
                  </div>
                  {stepIdx !== workflow.length - 1 && (
                    <div className={`ml-4 flex-1 h-0.5 ${
                      isComplete ? 'bg-indigo-600' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
                <div className="mt-2">
                  <span className={`text-sm font-medium ${
                    isComplete || isCurrent ? 'text-gray-900' : 'text-gray-500'
                  }`}>
                    {step.name}
                  </span>
                </div>
              </li>
            );
          })}
        </ol>
      </nav>

      {/* Current Status and Change To Section */}
      <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-600">Current Status:</span>
            <span className="text-sm font-semibold text-gray-900">
              {getDisplayLabel(currentState, problemStateLabels)}
            </span>
          </div>
          {/* Customer Approval Status - Show when in RCA state or when approval has been requested */}
          {(currentState === 'root_cause_analysis' || problem.customerApprovalRequested) && (
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-600">Fix Approval Status:</span>
              {problem.customerApprovalStatus === 'approved' ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Approved
                </span>
              ) : problem.customerApprovalStatus === 'rejected' ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Rejected
                </span>
              ) : problem.customerApprovalRequested ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  <svg className="w-3 h-3 mr-1 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  Pending
                </span>
              ) : (
                <span className="text-sm text-gray-500">Not Requested</span>
              )}
            </div>
          )}
        </div>

        {(() => {
          // Check for missing mandatory fields
          const missingFields: string[] = [];

          if (!problem.assignedTo) {
            missingFields.push('Assigned To');
          }
          if (!problem.User_Case_rcaPresentedByIdToUser) {
            missingFields.push('RCA Presented By');
          }
          if (!problem.problemDate) {
            missingFields.push('Problem Date');
          }
          if (!problem.reportedIssue?.trim()) {
            missingFields.push('Reported Issue');
          }
          if (!problem.actualServiceImpact?.trim()) {
            missingFields.push('Actual Service Impact');
          }
          if (!problem.briefAnalysis?.trim()) {
            missingFields.push('Brief Analysis');
          }
          if (!problem.rootCause?.trim()) {
            missingFields.push('Root Cause');
          }
          if (!problem.detailedAnalysis?.trim()) {
            missingFields.push('Detailed Analysis');
          }
          if (!problem.correctiveActions?.trim()) {
            missingFields.push('Corrective/Mitigate Actions');
          }
          if (!problem.chronologyOfEvents?.trim()) {
            missingFields.push('Chronology of Events');
          }

          // Debug logging to identify button visibility issues
          console.log('[WorkflowProgress] Button state:', {
            currentState,
            customerApprovalRequested: problem.customerApprovalRequested,
            customerApprovalStatus: problem.customerApprovalStatus,
            missingFieldsCount: missingFields.length,
            canChangeState
          });

          // In root_cause_analysis state, handle customer approval workflow
          if (currentState === 'root_cause_analysis') {
            // If already approved, show Resolved button (but disable if there are open tasks)
            if (problem.customerApprovalStatus === 'approved') {
              return canChangeState && (
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-600">Change to:</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => onStatusChange('resolved')}
                      disabled={hasOpenTasks}
                      className={`px-4 py-1.5 text-sm font-medium rounded transition-colors ${
                        hasOpenTasks
                          ? 'text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed'
                          : 'text-green-700 bg-white border border-green-300 hover:bg-green-50'
                      }`}
                      title={hasOpenTasks ? 'Close all problem tasks first' : 'Mark as resolved'}
                    >
                      Resolved
                    </button>
                    <button
                      onClick={() => onStatusChange('cancelled')}
                      className="px-4 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 rounded hover:bg-red-50 transition-colors"
                    >
                      Cancelled
                    </button>
                  </div>
                </div>
              );
            }

            // Not yet approved - show Communicate Fix or Resend Communicate Fix
            const buttonLabel = problem.customerApprovalRequested ? 'Resend Communicate Fix' : 'Communicate Fix';
            const isResend = problem.customerApprovalRequested === true;

            return canChangeState && (
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-600">Change to:</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      if (isResend) {
                        onResendApproval();
                      } else {
                        onStatusChange('resolved');
                      }
                    }}
                    className="px-4 py-1.5 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded hover:bg-blue-50 transition-colors"
                  >
                    {buttonLabel}
                  </button>
                  <button
                    onClick={() => onStatusChange('cancelled')}
                    className="px-4 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 rounded hover:bg-red-50 transition-colors"
                  >
                    Cancelled
                  </button>
                </div>
              </div>
            );
          }

          // Hide "Change to" buttons if there are missing fields (only for non-pending states)
          if (missingFields.length > 0) {
            return null;
          }

          // Determine if we should show Communicate Fix or Resolved based on approval status
          // Show Communicate Fix when:
          // 1. State is root_cause_analysis
          // 2. AND approval is NOT pending (not waiting for customer response)
          // 3. AND customer has NOT approved (otherwise show Resolved button)
          // Note: Communicate Fix is shown even with open tasks - user can request approval
          // This covers: first-time requests, re-requests after rejection, and any other non-pending/non-approved state
          const showCommunicateFix = currentState === 'root_cause_analysis' &&
            problem.customerApprovalStatus !== 'pending' &&
            problem.customerApprovalStatus !== 'approved';
          // Only show Resolved button when there are no open tasks
          // User cannot resolve if any problem task is in open state
          const showResolvedAfterApproval = currentState === 'root_cause_analysis' &&
            problem.customerApprovalStatus === 'approved' && !hasOpenTasks;
          const hideActionsForPending = problem.customerApprovalRequested &&
            problem.customerApprovalStatus === 'pending';

          return canChangeState && (availableTransitions.length > 0 || currentState !== 'cancelled') && (
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-600">Change to:</span>
              <div className="flex gap-2">
                {/* Hide all action buttons except Cancelled when customer approval is pending */}
                {!hideActionsForPending && availableTransitions.map((state) => {
                  // When in resolved state with open tasks, only allow Reopen (new), block Close
                  if (hasOpenTasks && currentState === 'resolved' && state === 'closed') {
                    return null; // Don't show Close button when there are open tasks
                  }

                  // Skip 'resolved' in normal flow if we need to show special buttons
                  if (state === 'resolved' && currentState === 'root_cause_analysis') {
                    // Show "Communicate Fix" if approval not requested yet
                    if (showCommunicateFix) {
                      return (
                        <button
                          key={state}
                          onClick={() => onStatusChange(state)}
                          className="px-4 py-1.5 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded hover:bg-blue-50 transition-colors"
                        >
                          Communicate Fix
                        </button>
                      );
                    }
                    // Show "Resolved" if customer has approved
                    if (showResolvedAfterApproval) {
                      return (
                        <button
                          key={state}
                          onClick={() => onStatusChange(state)}
                          className="px-4 py-1.5 text-sm font-medium text-green-700 bg-white border border-green-300 rounded hover:bg-green-50 transition-colors"
                        >
                          Resolved
                        </button>
                      );
                    }
                    // Don't show resolved button in other cases (approval pending)
                    return null;
                  }

                  // Special handling for "Reopen" button when in resolved state
                  if (state === 'new' && currentState === 'resolved') {
                    return (
                      <button
                        key={state}
                        onClick={() => onStatusChange(state)}
                        className="px-4 py-1.5 text-sm font-medium text-orange-700 bg-white border border-orange-300 rounded hover:bg-orange-50 transition-colors"
                      >
                        Reopen
                      </button>
                    );
                  }

                  return (
                    <button
                      key={state}
                      onClick={() => onStatusChange(state)}
                      className="px-4 py-1.5 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded hover:bg-blue-50 transition-colors"
                    >
                      {getDisplayLabel(state, problemStateLabels)}
                    </button>
                  );
                })}
                {/* Resend Approval button - show when approval is pending */}
                {problem.customerApprovalRequested && problem.customerApprovalStatus === 'pending' && (
                  <button
                    onClick={onResendApproval}
                    className="px-4 py-1.5 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded hover:bg-blue-50 transition-colors"
                  >
                    Resend Approval
                  </button>
                )}
                {currentState !== 'cancelled' && currentState !== 'closed' && currentState !== 'resolved' && (
                  <button
                    onClick={() => onStatusChange('cancelled')}
                    className="px-4 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 rounded hover:bg-red-50 transition-colors"
                  >
                    Cancelled
                  </button>
                )}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

// Separate component for tab content to avoid JSX complexity
interface TabContentProps {
  activeTab: string;
  problem: Problem;
  problemId: string;
  newNote: string;
  setNewNote: (note: string) => void;
  noteType: 'work_note' | 'customer_note';
  setNoteType: (type: 'work_note' | 'customer_note') => void;
  handleAddNote: (e: React.FormEvent) => void;
  isAddingNote: boolean;
  workNotes: WorkNote[];
  isLoadingNotes: boolean;
  canEdit: boolean;
  canAddWorkNote: boolean;
  canAddAttachments: boolean;
  canAddRelated: boolean;
  handleAssignToMe: () => void;
  shouldShowAssignToMeButton: () => boolean;
  isAssigning: boolean;
  isEditingProblemDate: boolean;
  editProblemDate: string;
  setEditProblemDate: (value: string) => void;
  handleSaveProblemDate: () => void;
  handleEditProblemDate: () => void;
  setIsEditingProblemDate: (value: boolean) => void;
  isUpdatingProblemDate: boolean;
  onTabChange: (tab: 'details' | 'assessment' | 'activity' | 'notes' | 'attachments' | 'activities' | 'related' | 'tasks' | 'closure') => void;
  user: any;
  id: string;
  isServiceDeskUser: boolean;
}

const TabContent: React.FC<TabContentProps> = ({
  activeTab,
  problem,
  problemId,
  newNote,
  setNewNote,
  noteType,
  setNoteType,
  handleAddNote,
  isAddingNote,
  workNotes,
  isLoadingNotes,
  canEdit,
  canAddWorkNote,
  canAddAttachments,
  canAddRelated,
  handleAssignToMe,
  shouldShowAssignToMeButton,
  isAssigning,
  isEditingProblemDate,
  editProblemDate,
  setEditProblemDate,
  handleSaveProblemDate,
  handleEditProblemDate,
  setIsEditingProblemDate,
  isUpdatingProblemDate,
  onTabChange,
  user,
  id,
  isServiceDeskUser
}) => {
  const { formatDate, formatDateTime } = useDateFormatter();

  if (activeTab === 'details') {
    return (
      <DetailsTab
        problem={problem}
        handleAssignToMe={handleAssignToMe}
        shouldShowAssignToMeButton={shouldShowAssignToMeButton}
        isAssigning={isAssigning}
        isEditingProblemDate={isEditingProblemDate}
        editProblemDate={editProblemDate}
        setEditProblemDate={setEditProblemDate}
        handleSaveProblemDate={handleSaveProblemDate}
        handleEditProblemDate={handleEditProblemDate}
        setIsEditingProblemDate={setIsEditingProblemDate}
        isUpdatingProblemDate={isUpdatingProblemDate}
      />
    );
  } else if (activeTab === 'assessment') {
    return <AssessmentTab problem={problem} />;
  } else if (activeTab === 'notes' || activeTab === 'activity') {
    return (
      <NotesTab
        workNotes={workNotes}
        isLoadingNotes={isLoadingNotes}
        newNote={newNote}
        setNewNote={setNewNote}
        noteType={noteType}
        setNoteType={setNoteType}
        handleAddNote={handleAddNote}
        isAddingNote={isAddingNote}
        canAddWorkNote={canAddWorkNote}
      />
    );
  } else if (activeTab === 'attachments') {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <AttachmentTab
          entityType="case"
          entityId={problemId}
          canUpload={canAddAttachments}
          canDelete={canAddAttachments}
          ticketState={problem?.problemState}
          onAttachmentAdded={() => {
            if (user && problem) {
              trackProblemOperation(
                'Problem Details - Edit',
                user.email || user.firstName || 'Unknown',
                {
                  action: 'Added Attachments',
                  problemNumber: problem.number || problem.id || id || 'Unknown',
                },
                {
                firstName: user.firstName,
                lastName: user.lastName,
                role: user.role,
                roleType: user.roleType,
                roleLevel: user.roleLevel,
                userClass: user.class,
                customerId: user.customerId,
                customerName: user.customerName,
                engagementId: user.engagementId,
                engagementName: user.engagementName,
                isServiceDeskUser: isServiceDeskUser,
                }
              );
            }
          }}
        />
      </div>
    );
  } else if (activeTab === 'activities') {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <ActivityTab entityId={problemId} onTabChange={onTabChange as (tab: string) => void} />
      </div>
    );
  } else if (activeTab === 'related') {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <RelatedRecordsTab
          caseId={problemId}
          caseNumber={problem?.number || ''}
          ticketType="PROBLEM"
          canManage={canAddRelated}
          onRelatedRecordAdded={() => {
            if (user && problem) {
              trackProblemOperation(
                'Problem Details - Edit',
                user.email || user.firstName || 'Unknown',
                {
                  action: 'Added Related Record',
                  problemNumber: problem.number || problem.id || id || 'Unknown',
                },
                {
                firstName: user.firstName,
                lastName: user.lastName,
                role: user.role,
                roleType: user.roleType,
                roleLevel: user.roleLevel,
                userClass: user.class,
                customerId: user.customerId,
                customerName: user.customerName,
                engagementId: user.engagementId,
                engagementName: user.engagementName,
                isServiceDeskUser: isServiceDeskUser,
                }
              );
            }
          }}
          onRelatedRecordDeleted={() => {
            if (user && problem) {
              trackProblemOperation(
                'Problem Details - Edit',
                user.email || user.firstName || 'Unknown',
                {
                  action: 'Removed Related Record',
                  problemNumber: problem.number || problem.id || id || 'Unknown',
                },
                {
                firstName: user.firstName,
                lastName: user.lastName,
                role: user.role,
                roleType: user.roleType,
                roleLevel: user.roleLevel,
                userClass: user.class,
                customerId: user.customerId,
                customerName: user.customerName,
                engagementId: user.engagementId,
                engagementName: user.engagementName,
                isServiceDeskUser: isServiceDeskUser,
                }
              );
            }
          }}
        />
      </div>
    );
  } else if (activeTab === 'tasks') {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <ProblemTasksTab
          problemId={problemId}
          problemNumber={problem?.number || ''}
          engagementId={problem?.engagementId || null}
          customerId={problem?.customerId || problem?.Customer?.id || null}
          hideNewButton={problem.customerApprovalRequested || !canEdit}
        />
      </div>
    );
  } else if (activeTab === 'closure') {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-gray-900 border-b pb-3">Closure Information</h3>

          {/* Resolution Info Grid */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Resolved By</label>
              <p className="text-sm text-gray-900">
                {problem.resolvedBy
                  ? `${problem.resolvedBy.firstName || ''} ${problem.resolvedBy.lastName || ''}`.trim() || '-'
                  : '-'}
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Resolved On</label>
              <p className="text-sm text-gray-900">
                {problem.resolvedAt ? formatDateTime(problem.resolvedAt) : '-'}
              </p>
            </div>
          </div>

          {/* Closure Info Grid */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Closed By</label>
              <p className="text-sm text-gray-900">
                {problem.closedBy
                  ? `${problem.closedBy.firstName || ''} ${problem.closedBy.lastName || ''}`.trim() || '-'
                  : '-'}
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Closed On</label>
              <p className="text-sm text-gray-900">
                {problem.closedAt ? formatDateTime(problem.closedAt) : '-'}
              </p>
            </div>
          </div>

          {/* Closure Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Closure Notes
            </label>
            {problem.closureNotes ? (
              <div className="w-full px-4 py-3 border border-green-300 rounded-lg bg-green-50 text-sm">
                <RichTextDisplay content={problem.closureNotes} />
              </div>
            ) : (
              <div className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-500 italic">
                No closure notes provided
              </div>
            )}
          </div>

          {/* Resolution/Solution */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Resolution
            </label>
            {(problem.resolution || problem.resolutionNotes) ? (
              <div className="w-full px-4 py-3 border border-green-300 rounded-lg bg-green-50 text-sm">
                <RichTextDisplay content={problem.resolution || problem.resolutionNotes || ''} />
              </div>
            ) : (
              <div className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-500 italic">
                No resolution provided
              </div>
            )}
          </div>

          {/* Solution */}
          {problem.solution && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Solution
              </label>
              <div className="w-full px-4 py-3 border border-blue-300 rounded-lg bg-blue-50 text-sm">
                <RichTextDisplay content={problem.solution} />
              </div>
            </div>
          )}

          {/* Root Cause */}
          {problem.rootCause && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Root Cause
              </label>
              <div className="w-full px-4 py-3 border border-amber-300 rounded-lg bg-amber-50 text-sm">
                <RichTextDisplay content={problem.rootCause} />
              </div>
            </div>
          )}

          {/* Implementation Date */}
          {problem.implementedDate && (
            <div className="text-sm text-gray-600">
              <span className="font-medium">Implementation Date:</span> {formatDate(problem.implementedDate)}
            </div>
          )}

          {/* Summary Section */}
          {(problem.problemState === 'resolved' || problem.problemState === 'closed' || problem.problemState === 'cancelled') && (
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    problem.problemState === 'cancelled'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {problem.problemState === 'resolved' ? 'Resolved' :
                     problem.problemState === 'closed' ? 'Closed' : 'Cancelled'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Empty State when no closure info */}
          {problem.problemState !== 'resolved' && problem.problemState !== 'closed' && problem.problemState !== 'cancelled' && (
            <div className="text-center py-8 text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="mt-2 text-sm">This problem has not been resolved or closed yet.</p>
              <p className="mt-1 text-xs text-gray-400">Closure information will appear here once the problem is resolved.</p>
            </div>
          )}
        </div>
      </div>
    );
  }
  return null;
};

const DetailsTab: React.FC<{
  problem: Problem;
  handleAssignToMe: () => void;
  shouldShowAssignToMeButton: () => boolean;
  isAssigning: boolean;
  isEditingProblemDate: boolean;
  editProblemDate: string;
  setEditProblemDate: (value: string) => void;
  handleSaveProblemDate: () => void;
  handleEditProblemDate: () => void;
  setIsEditingProblemDate: (value: boolean) => void;
  isUpdatingProblemDate: boolean;
}> = ({
  problem,
  handleAssignToMe,
  shouldShowAssignToMeButton,
  isAssigning,
  isEditingProblemDate,
  editProblemDate,
  setEditProblemDate,
  handleSaveProblemDate,
  handleEditProblemDate,
  setIsEditingProblemDate,
  isUpdatingProblemDate
}) => {
  const { formatDate, formatDateTime } = useDateFormatter();

  return (
  <div className="space-y-4">
    <h3 className="text-lg font-medium text-gray-900 mb-4">Problem Details</h3>

    {/* First Row - Number and Opened */}
    <div className="grid grid-cols-2 gap-4">
      <div className="flex">
        <label className="text-sm font-medium text-gray-600 w-40">Number</label>
        <div className="flex-1 px-3 py-2 bg-gray-50 rounded border border-gray-200">
          <span className="text-sm text-gray-900">{problem.number}</span>
        </div>
      </div>
      <div className="flex">
        <label className="text-sm font-medium text-gray-600 w-40">Opened</label>
        <div className="flex-1 px-3 py-2 bg-gray-50 rounded border border-gray-200">
          <span className="text-sm text-gray-900">{formatDateTime(problem.createdAt)}</span>
        </div>
      </div>
    </div>

    {/* Second Row - State and Impact */}
    <div className="grid grid-cols-2 gap-4">
      <div className="flex">
        <label className="text-sm font-medium text-gray-600 w-40">State</label>
        <div className="flex-1 px-3 py-2 bg-gray-50 rounded border border-gray-200">
          <span className="text-sm text-gray-900">
            {getDisplayLabel(problem.problemState || 'new', problemStateLabels)}
          </span>
        </div>
      </div>
      <div className="flex">
        <label className="text-sm font-medium text-gray-600 w-40">Impact</label>
        <div className="flex-1 px-3 py-2 bg-gray-50 rounded border border-gray-200">
          <span className="text-sm text-gray-900">
            {getDisplayLabel(problem.impact, impactLabels)}
          </span>
        </div>
      </div>
    </div>

    {/* Third Row - Urgency and Priority */}
    <div className="grid grid-cols-2 gap-4">
      <div className="flex">
        <label className="text-sm font-medium text-gray-600 w-40">Urgency</label>
        <div className="flex-1 px-3 py-2 bg-gray-50 rounded border border-gray-200">
          <span className="text-sm text-gray-900">
            {getDisplayLabel(problem.urgency, urgencyLabels)}
          </span>
        </div>
      </div>
      <div className="flex">
        <label className="text-sm font-medium text-gray-600 w-40">Priority (Auto-calculated)</label>
        <div className="flex-1 px-3 py-2 bg-blue-50 rounded border border-blue-200">
          <span className="text-sm text-blue-800 font-medium">
            {getDisplayLabel(problem.priority, priorityLabels)}
          </span>
        </div>
      </div>
    </div>

    {/* Fourth Row - Customer and Engagement */}
    <div className="grid grid-cols-2 gap-4">
      <div className="flex">
        <label className="text-sm font-medium text-gray-600 w-40">Customer</label>
        <div className="flex-1 px-3 py-2 bg-gray-50 rounded border border-gray-200">
          <span className="text-sm text-gray-900">
            {problem.Customer?.accountName || problem.Customer?.name || problem.customer?.name || 'Not Assigned'}
          </span>
        </div>
      </div>
      <div className="flex">
        <label className="text-sm font-medium text-gray-600 w-40">Engagement</label>
        <div className="flex-1 px-3 py-2 bg-gray-50 rounded border border-gray-200">
          <span className="text-sm text-gray-900">
            {problem.Engagement?.name || problem.engagement?.name || 'Not Assigned'}
          </span>
        </div>
      </div>
    </div>

    {/* Fifth Row - Contact */}
    <div className="grid grid-cols-2 gap-4">
      <div className="flex">
        <label className="text-sm font-medium text-gray-600 w-40">Contact</label>
        <div className="flex-1 px-3 py-2 bg-gray-50 rounded border border-gray-200">
          <span className="text-sm text-gray-900">
            {(() => {
              const contact = problem.contact || problem.User_Case_contactIdToUser;
              return contact ? `${contact.firstName} ${contact.lastName}` : 'Not Specified';
            })()}
          </span>
        </div>
      </div>
    </div>

    {/* Sixth Row - Resource and Resource ID / Custom Resources */}
    <div className="grid grid-cols-2 gap-4">
      <div className="flex">
        <label className="text-sm font-medium text-gray-600 w-40">Resource</label>
        <div className="flex-1 px-3 py-2 bg-gray-50 rounded border border-gray-200">
          <span className="text-sm text-gray-900">
            {problem.otherResourceNames
              ? 'Others (Custom)'
              : (problem.Resource?.name || problem.resource?.name || 'Not Assigned')}
          </span>
        </div>
      </div>
      <div className="flex">
        <label className="text-sm font-medium text-gray-600 w-40">
          {problem.otherResourceNames ? 'Custom Resources' : 'Resource ID'}
        </label>
        <div className="flex-1">
          <input
            type="text"
            value={problem.otherResourceNames || problem.resourceIdValue || problem.Resource?.resourceId || ''}
            readOnly
            className="w-full px-3 py-1 border border-gray-300 rounded bg-gray-100 text-gray-600 text-sm h-8 truncate"
            placeholder="Not specified"
            title={problem.otherResourceNames || problem.resourceIdValue || problem.Resource?.resourceId || ''}
          />
        </div>
      </div>
    </div>

    {/* Seventh Row - Assignment Group and Assigned To */}
    <div className="grid grid-cols-2 gap-4">
      <div className="flex">
        <label className="text-sm font-medium text-gray-600 w-40">Assignment Group <span className="text-red-500">*</span></label>
        <div className="flex-1 px-3 py-2 bg-gray-50 rounded border border-gray-200">
          <span className="text-sm text-gray-900">
            {problem.AssignmentGroup?.name || problem.assignmentGroup?.name || 'Not specified'}
          </span>
        </div>
      </div>
      <div className="flex">
        <label className="text-sm font-medium text-gray-600 w-40">Assigned To</label>
        <div className="flex-1 flex gap-2">
          <div className="flex-1 px-3 py-2 bg-gray-50 rounded border border-gray-200">
            <span className="text-sm text-gray-900">
              {problem.assignedTo ?
                `${problem.assignedTo.firstName} ${problem.assignedTo.lastName}` :
                'Unassigned'
              }
            </span>
          </div>
          {shouldShowAssignToMeButton() && (
            <button
              onClick={handleAssignToMe}
              disabled={isAssigning}
              className="px-3 py-1 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded disabled:opacity-50 whitespace-nowrap h-8"
            >
              {isAssigning ? 'Assigning...' : 'Assign To Me'}
            </button>
          )}
        </div>
      </div>
    </div>

    {/* Reference Alert */}
    {problem.ReferenceAlert && (
      <div className="grid grid-cols-2 gap-4">
        <div className="flex">
          <label className="text-sm font-medium text-gray-600 w-40">Reference Alert</label>
          <div className="flex-1 px-3 py-2 bg-gray-50 rounded border border-gray-200">
            <Link
              to={`/alerts/${problem.ReferenceAlert.id}`}
              className="inline-flex items-center px-3 py-1 border border-blue-300 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 text-sm h-8"
            >
              <TicketIcon className="h-4 w-4 mr-1" />
              {problem.ReferenceAlert.number}
            </Link>
          </div>
        </div>
      </div>
    )}

    {/* Short Description */}
    <div className="flex">
      <label className="text-sm font-medium text-gray-600 w-40 pt-2">Short Description</label>
      <div className="flex-1">
        <ResizableTextDisplay
          content={problem.shortDescription || ''}
          defaultHeight={60}
          minHeight={40}
          maxHeight={300}
        />
      </div>
    </div>

    {/* Description/Problem Statement */}
    <div className="flex">
      <label className="text-sm font-medium text-gray-600 w-40 pt-2">Problem Statement</label>
      <div className="flex-1">
        <ResizableTextDisplay
          content={problem.problemStatement || problem.description || 'No problem statement provided'}
          defaultHeight={100}
          minHeight={60}
          maxHeight={500}
        />
      </div>
    </div>

    {/* Review Matrix Section */}
    <div className="mt-8 pt-6 border-t border-gray-200">
      <h3 className="text-lg font-medium text-gray-900 mb-6">Review Matrix</h3>

      {/* Row 1: RCA Presented By and Problem Date */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="flex">
          <label className="text-sm font-medium text-gray-600 w-40">RCA Presented By</label>
          <div className="flex-1 px-3 py-2 bg-gray-50 rounded border border-gray-200">
            <span className="text-sm text-gray-900">
              {problem.User_Case_rcaPresentedByIdToUser ?
                `${problem.User_Case_rcaPresentedByIdToUser.firstName} ${problem.User_Case_rcaPresentedByIdToUser.lastName}` :
                'Not Specified'
              }
            </span>
          </div>
        </div>
        <div className="flex">
          <label className="text-sm font-medium text-gray-600 w-40">Problem Date</label>
          <div className="flex-1">
            {isEditingProblemDate ? (
              <div className="flex items-center gap-2">
                <DateTimePickerInput
                  value={editProblemDate}
                  onChange={(val) => setEditProblemDate(val)}
                  max={new Date().toISOString().split('T')[0]}
                  showTimeSelect={false}
                  className="w-48"
                />
                <button
                  onClick={handleSaveProblemDate}
                  disabled={isUpdatingProblemDate}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400"
                >
                  {isUpdatingProblemDate ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setIsEditingProblemDate(false)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="flex-1 px-3 py-2 bg-gray-50 rounded border border-gray-200">
                  <span className="text-sm text-gray-900">
                    {problem.problemDate ? formatDate(problem.problemDate) : 'Not Specified'}
                  </span>
                </div>
                {problem.canEdit && (
                  <button
                    onClick={handleEditProblemDate}
                    className="inline-flex items-center p-2 text-gray-500 hover:text-indigo-600 focus:outline-none"
                    title="Edit Problem Date"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Row 2: Reported Issue */}
      <div className="flex mb-6">
        <label className="text-sm font-medium text-gray-600 w-40 pt-2">Reported Issue</label>
        <div className="flex-1 px-3 py-2 bg-gray-50 rounded border border-gray-200 min-h-[80px]">
          <RichTextDisplay content={problem.reportedIssue || 'Not Specified'} />
        </div>
      </div>

      {/* Row 3: Actual Service Impact */}
      <div className="flex mb-6">
        <label className="text-sm font-medium text-gray-600 w-40 pt-2">Actual Service Impact</label>
        <div className="flex-1 px-3 py-2 bg-gray-50 rounded border border-gray-200 min-h-[80px]">
          <RichTextDisplay content={problem.actualServiceImpact || 'Not Specified'} />
        </div>
      </div>

      {/* Row 4: Brief Analysis */}
      <div className="flex mb-6">
        <label className="text-sm font-medium text-gray-600 w-40 pt-2">Brief Analysis</label>
        <div className="flex-1 px-3 py-2 bg-gray-50 rounded border border-gray-200 min-h-[80px]">
          <RichTextDisplay content={problem.briefAnalysis || 'Not Specified'} />
        </div>
      </div>

      {/* Row 5: Root Cause */}
      <div className="flex mb-6">
        <label className="text-sm font-medium text-gray-600 w-40 pt-2">Root Cause</label>
        <div className="flex-1 px-3 py-2 bg-gray-50 rounded border border-gray-200 min-h-[80px]">
          <RichTextDisplay content={problem.rootCause || 'Not Specified'} />
        </div>
      </div>

      {/* Row 6: Detailed Analysis */}
      <div className="flex mb-6">
        <label className="text-sm font-medium text-gray-600 w-40 pt-2">Detailed Analysis</label>
        <div className="flex-1 px-3 py-2 bg-gray-50 rounded border border-gray-200 min-h-[100px]">
          <RichTextDisplay content={problem.detailedAnalysis || 'Not Specified'} />
        </div>
      </div>

      {/* Row 6: Corrective/Mitigate Actions */}
      <div className="flex mb-6">
        <label className="text-sm font-medium text-gray-600 w-40 pt-2">Corrective/Mitigate Actions</label>
        <div className="flex-1 px-3 py-2 bg-gray-50 rounded border border-gray-200 min-h-[80px]">
          <RichTextDisplay content={problem.correctiveActions || 'Not Specified'} />
        </div>
      </div>

      {/* Row 7: Chronology of Events */}
      <div className="flex mb-6">
        <label className="text-sm font-medium text-gray-600 w-40 pt-2">Chronology of Events</label>
        <div className="flex-1 px-3 py-2 bg-gray-50 rounded border border-gray-200 min-h-[100px]">
          <RichTextDisplay content={problem.chronologyOfEvents || 'Not Specified'} />
        </div>
      </div>
    </div>
  </div>
  );
};


const AssessmentTab: React.FC<{ problem: Problem }> = ({ problem }) => (
  <div className="space-y-6">
    <h3 className="text-lg font-medium text-gray-900">Assessment Summary</h3>

    {/* Assessment 1 Summary */}
    <div className="bg-gray-50 rounded-lg p-6">
      <h4 className="text-base font-medium text-gray-900 mb-3">Assessment 1</h4>
      <div className="text-sm text-gray-700">
        {problem.assessment1Summary ? (
          <RichTextDisplay content={problem.assessment1Summary} />
        ) : (
          <p className="text-gray-500 italic">No assessment summary available</p>
        )}
      </div>
    </div>

    {/* Assessment 2 Summary */}
    <div className="bg-gray-50 rounded-lg p-6">
      <h4 className="text-base font-medium text-gray-900 mb-3">Assessment 2</h4>
      <div className="text-sm text-gray-700">
        {problem.assessment2Summary ? (
          <RichTextDisplay content={problem.assessment2Summary} />
        ) : (
          <p className="text-gray-500 italic">No assessment summary available</p>
        )}
      </div>
    </div>
  </div>
);

interface NotesTabProps {
  workNotes: WorkNote[];
  isLoadingNotes?: boolean;
  newNote: string;
  setNewNote: (note: string) => void;
  noteType: 'work_note' | 'customer_note';
  setNoteType: (type: 'work_note' | 'customer_note') => void;
  handleAddNote: (e: React.FormEvent) => void;
  isAddingNote: boolean;
  canAddWorkNote: boolean;
}

const NotesTab: React.FC<NotesTabProps> = ({
  workNotes,
  isLoadingNotes = false,
  newNote,
  setNewNote,
  noteType,
  setNoteType,
  handleAddNote,
  isAddingNote,
  canAddWorkNote
}) => {
  const { isContact } = useAuth();
  const { formatDateTime } = useDateFormatter();

  return (
    <div>
      {/* Add Note Form */}
      {canAddWorkNote && (
        <form onSubmit={handleAddNote} className="mb-6">
        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700">Add Note</label>
          <RichTextEditor
            value={newNote}
            onChange={(value) => setNewNote(value)}
            placeholder="Enter your note..."
            minHeight="120px"
          />
        </div>

        <div className="flex items-center justify-between">
          {!isContact() ? (
            <div className="flex items-center space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="work_note"
                  checked={noteType === 'work_note'}
                  onChange={(e) => setNoteType(e.target.value as 'work_note')}
                  className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">Work Note</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="customer_note"
                  checked={noteType === 'customer_note'}
                  onChange={(e) => setNoteType(e.target.value as 'customer_note')}
                  className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">Customer Note</span>
              </label>
            </div>
          ) : (
            <p className="text-sm text-gray-600 italic">Your notes will be visible to all users</p>
          )}

          <button
            type="submit"
            disabled={isAddingNote || !newNote.trim()}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {isAddingNote ? 'Adding...' : 'Add Note'}
          </button>
        </div>
      </form>
      )}

      {/* Notes List */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Work Notes</h3>
        {isLoadingNotes ? (
          <TabLoadingSpinner label="Loading work notes" />
        ) : workNotes && workNotes.length > 0 ? (
          <div className="space-y-4">
            {workNotes
              .filter((note: WorkNote) => isContact() ? note.isPublic : true)
              .map((note: WorkNote) => (
            <div key={note.id} className="border border-gray-200 rounded-lg p-4 bg-white">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                      <span className="text-sm font-medium text-gray-600">
                        {note.User?.firstName?.charAt(0).toUpperCase() || 'U'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-900">
                      {note.User ? `${note.User.firstName} ${note.User.lastName}` : 'Unknown User'}
                    </span>
                    <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      note.isPublic ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {note.isPublic ? 'Public' : 'Internal'}
                    </span>
                  </div>
                </div>
                <span className="text-xs text-gray-500">
                  {formatDateTime(note.timestamp)}
                </span>
              </div>
              <div className="ml-11">
                <div className="text-sm text-gray-700">
                  <RichTextDisplay content={note.note} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-sm text-gray-500">No work notes yet</p>
          <p className="mt-1 text-xs text-gray-400">Work notes will appear here once added</p>
        </div>
      )}
    </div>
  </div>
  );
};

export default ProblemDetail;